import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Grid,
  Chip,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Link from "@mui/material/Link";

import {
  ArtButton,
  ArtDialog,
  useApiService,
  useIdpService,
} from "@art/shared-ui";
import { useSelector, useDispatch } from "react-redux";
import { RootState, updateLicenseState } from "../../../store";
import { usePageWrapper } from "../../../hooks";
import {
  AddonOrderSummaryDialog,
  AddonPurchaseConfirmation,
  PaymentSuccessDialog,
  UpgradeAllertDialog,
  buildPaymentAttemptStorageKey,
  clearPaymentAttemptKey,
  getPaymentAttemptKey,
  APP_CATEGORY_META,
  DEFAULT_APP_CATEGORY_META,
} from "./helpers";

import ActiveAddons from "../../../components/addons/active-addons";
import AvailableAddons from "../../../components/addons/available-addons";
import { useAttributePermission } from "../../permissions";
import {
  isSandboxLicense,
  licenseStateFromProfile,
  selectApplicationLicense,
} from "../../../utils/license";



interface DialogData {
  title: string;
  content: React.ReactNode;
}

const getUtcDateOnly = (date: Date) =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

const getAddonCycleDays = (startsAt: Date, expiryDate: Date) => {
  return Math.floor(
    (getUtcDateOnly(expiryDate) - getUtcDateOnly(startsAt)) /
    (1000 * 60 * 60 * 24),
  );
};

const getAddonRemainingDays = (expiryDate: Date, now = new Date()) => {
  return Math.floor(
    (getUtcDateOnly(expiryDate) - getUtcDateOnly(now)) /
    (1000 * 60 * 60 * 24),
  );
};



const AddonsPage = () => {
  const canManagePayments = useAttributePermission("PLATFORM_SETTINGS", "BILLING_USAGE", "ManagePayments");
  const theme = useTheme();
  const apiservice = useApiService();
  const idpService = useIdpService();
  const dispatch = useDispatch();
  const tenantName = useSelector(
    (state: RootState) => state.auth.tenantName
  );
  const userPromise = useSelector((state: RootState) => state.auth.user);
  const { licenseConfig } = useSelector(
    (state: RootState) => state.license
  );
  const currentPlan = licenseConfig;

  const buildAddonPaymentAttemptKey = (addonId: string | number, quantity: number) =>
    buildPaymentAttemptStorageKey(
      "addon",
      tenantName || "",
      String(addonId),
      String(quantity),
    );

  const initiateAddonPayment = async (
    payload: any,
    storageKey: string,
    retry = true,
  ) => {
    const paymentAttemptKey = getPaymentAttemptKey(storageKey);
    try {
      return await apiservice.post(
        `/payment/addon/initiate`,
        { ...payload, idempotency_key: paymentAttemptKey },
        {
          headers: {
            "Idempotency-Key": paymentAttemptKey,
          },
        },
      );
    } catch (error: any) {
      const data = error?.response?.data || {};
      const errorCode = data.error_code || data.code || data.errorCode;

      if (retry && errorCode === "PAYMENT_ATTEMPT_EXPIRED") {
        clearPaymentAttemptKey(storageKey);
        return initiateAddonPayment(payload, storageKey, false);
      }

      if (retry && errorCode === "IDEMPOTENCY_KEY_REQUIRED") {
        clearPaymentAttemptKey(storageKey);
        return initiateAddonPayment(payload, storageKey, false);
      }

      if (errorCode === "PAYMENT_ATTEMPT_CLOSED") {
        clearPaymentAttemptKey(storageKey);
        apiservice.showSnack(
          "This payment attempt has been closed. Please start again.",
          "error",
        );
      }

      if (errorCode === "IDEMPOTENCY_KEY_REUSED") {
        clearPaymentAttemptKey(storageKey);
        apiservice.showSnack(
          "The payment attempt key was reused. Please start a fresh payment attempt.",
          "error",
        );
      }

      throw error;
    }
  };

  const cancelPaymentAttempt = (orderId?: string, reason?: string) => {
    if (!orderId) return;
    apiservice
      .post("/payment/addon/attempt/cancel", {
        razorpay_order_id: orderId,
        reason,
      })
      .catch((err: any) => {
        console.error("Failed to record payment attempt cancellation:", err);
      });
  };

  const [activeTab, setActiveTab] = useState(0);
  const [availableAddons, setAvailableAddons] = useState<any[]>([]);
  const [activeAddons, setActiveAddons] = useState<any[]>([]);
  const [addonsLoading, setAddonsLoading] = useState(true);
  const [, setIsPaying] = useState(false);
  const [isCancellingAddon, setIsCancellingAddon] = useState(false);
  const [selectedApplicationCode, setSelectedApplicationCode] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<DialogData | null>(null);
  const [dialogSize, setDialogSize] = useState<"small" | "medium" | "large">("medium");


  const handleOpenDialog = (title: string, content: React.ReactNode) => {
    setDialogData({ title, content });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setDialogData(null);
  };

  const fetchAvailableAddons = async () => {
    try {
      const response = await idpService.get(
        `api/tenants/${tenantName}/addons/available?application_code=ALL`
      );
      setAvailableAddons(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching available addons:", error);
    }
  };

  const fetchActiveAddons = async () => {
    try {
      const response = await idpService.get(
        `api/tenants/${tenantName}/addons/active?application_code=ALL`
      );
      setActiveAddons(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching active addons:", error);
    }
  };

  const handleCancelAddon = async (
    purchasedAddonId: string,
    addonName: string,
    applicationCode: string,
  ) => {
    setIsCancellingAddon(true);
    try {
      const response = await apiservice.post(
        `/payment/addon/cancel`,
        {
          purchased_addon_id: purchasedAddonId,
          application_code: applicationCode,
        }
      );
      if (response?.data) {
        apiservice.showSnack(
          `${addonName} cancelled successfully. It will remain active until its expiry date.`,
          "success"
        );
        handleClose();
        fetchActiveAddons();
      }
    } catch (error: any) {
      apiservice.showSnack(
        error?.response?.data?.message || "Failed to cancel addon",
        "error"
      );
    } finally {
      setIsCancellingAddon(false);
    }
  };

  const handleConfirmCancelAddon = (addon: any) => {
    const addonName = addon.addon?.name || "this add-on";
    const validUntil = addon.valid_until
      ? new Date(addon.valid_until).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;

    setDialogSize("medium");
    handleOpenDialog(
      "Cancel Add-on",
      <Box>
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.error.main, 0.12),
              flexShrink: 0,
            }}
          >
            <WarningAmberRoundedIcon color="error" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              Cancel <span style={{ textTransform: "capitalize" }}>{addonName}</span>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              This stops auto-renewal. You keep access until the add-on expires.
            </Typography>
          </Box>
        </Stack>

        {/* Info cards */}
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.04),
              borderColor: alpha(theme.palette.info.main, 0.3),
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <AccessTimeRoundedIcon color="info" fontSize="small" sx={{ mt: 0.3 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  Stays active until {validUntil || "its expiry date"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The extra limits this add-on provides remain fully usable
                  during the current cycle. You won't be billed again.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.warning.main, 0.04),
              borderColor: alpha(theme.palette.warning.main, 0.3),
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <InfoOutlinedIcon color="warning" fontSize="small" sx={{ mt: 0.3 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                  After {validUntil || "expiry"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The add-on's extra limits will no longer apply and it won't
                  auto-renew. You can re-purchase it anytime.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>

        {/* Actions */}
        <Box display="flex" justifyContent="flex-start" gap={1.5} sx={{ whiteSpace: "nowrap" }}>
          <ArtButton
            variant="outlined"
            label="Keep Add-on"
            onClick={handleClose}
            disabled={isCancellingAddon}
          />
          <ArtButton
            variant="contained"
            color="error"
            label={isCancellingAddon ? "Cancelling..." : "Cancel Add-on"}
            disabled={isCancellingAddon}
            onClick={() => handleCancelAddon(
              addon.id,
              addonName,
              addon.addon?.feature?.application?.code || "",
            )}
          />
        </Box>
      </Box>
    );
  };

  // Razorpay script loader
  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleAddonPayment = async (addon: any, quantity: number) => {
    if (!canManagePayments) return;
    setIsPaying(true);
    try {
      // Fetch billing address to get contact number
      let contact: string | undefined;
      try {
        const addrResp = await apiservice.get("/organisation/billing-address");
        contact = addrResp?.data?.data?.contact;
      } catch (err) {
        console.error("Failed to fetch billing address:", err);
      }

      // Step 1: Initiate addon payment
      const storageKey = buildAddonPaymentAttemptKey(addon.id, quantity);
      const initiateResponse = await initiateAddonPayment(
        {
          addon_id: addon.id,
          quantity,
          application_code: addon.feature?.application?.code,
          email: userPromise?.email,
          contact,
        },
        storageKey,
      );

      const paymentData = initiateResponse?.data?.data;
      if (!paymentData?.order_id) {
        apiservice.showSnack("Failed to create addon payment order", "error");
        setIsPaying(false);
        return;
      }

      // Step 2: Load Razorpay
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        apiservice.showSnack("Failed to load payment gateway", "error");
        setIsPaying(false);
        return;
      }

      // Step 3: Open Razorpay checkout
      const options = {
        key: paymentData.razorpay_key_id,
        order_id: paymentData.order_id,
        customer_id: paymentData.customer_id,
        recurring: 1,
        name: "ART Console",
        description: `${addon.name} × ${quantity}`,
        prefill: {
          name: userPromise?.first_name || "",
          email: userPromise?.email || "",
        },
        theme: { color: "#1976d2" },
        handler: async (response: any) => {
          // Step 4: Verify payment
          try {
            const verifyPayload = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            };

            const verifyResponse = await apiservice.post(
              `payment/addon/verify`,
              verifyPayload
            );

            if (verifyResponse?.status === 200 || verifyResponse?.status === 201) {
              clearPaymentAttemptKey(
                buildAddonPaymentAttemptKey(addon.id, quantity),
              );

              // Refresh license state
              try {
                const { data: { data } } = await apiservice.get("/user/profile");
                dispatch(updateLicenseState(licenseStateFromProfile(data)));
              } catch (err) {
                console.error("Failed to refresh license state:", err);
              }

              // Refresh addons list
              fetchActiveAddons();
              fetchAvailableAddons();

              // Show success dialog
              setDialogSize("small");
              handleOpenDialog(
                "Payment Successful",
                <PaymentSuccessDialog
                  selectedPlan={{
                    planTitle: `${addon.name} × ${quantity}`,
                  }}
                  paymentId={response.razorpay_payment_id}
                  onClose={handleClose}
                />
              );
            }
          } catch (verifyError: any) {
            console.error("Addon payment verification failed:", verifyError);
            apiservice.showSnack(
              verifyError?.response?.data?.message || "Payment verification failed",
              "error"
            );
          } finally {
            setIsPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setIsPaying(false);
            apiservice.showSnack("Payment cancelled", "info");
            cancelPaymentAttempt(paymentData.order_id, "user_dismissed_checkout");
            clearPaymentAttemptKey(storageKey);
          },
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (error: any) {
      console.error("Error initiating addon payment:", error);
      apiservice.showSnack(
        error?.response?.data?.message || "Failed to initiate payment",
        "error"
      );
      setIsPaying(false);
    }
  };

  const openAddonOrderSummary = (addon: any, quantity: number) => {
    if (!canManagePayments) return;
    setDialogSize("large");
    handleOpenDialog(
      "Complete Your Purchase",
      <AddonOrderSummaryDialog
        addon={addon}
        quantity={quantity}
        onBack={() => handleSimulateAddonLocal(addon)}
        onProceedToPayment={(a: any, q: number) => {
          handleAddonPayment(a, q);
        }}
      />,
    );
  };

  const handleSimulateAddonLocal = (addon: any) => {
    if (!canManagePayments) return;
    try {
      // Each add-on belongs to a specific application (Live Connect, Agent
      // Lab, ...), and a tenant can hold a different subscription per
      // application at once. The globally-cached currentPlan reflects
      // whichever app was last active elsewhere in the console (this page
      // isn't under an app-specific route), so it can point at the wrong
      // line entirely — resolve the license for THIS add-on's application
      // instead of assuming currentPlan is the right one.
      const addonAppCode = addon?.feature?.application?.code;
      const addonLicense =
        (addonAppCode && selectApplicationLicense(currentPlan, addonAppCode)) ||
        currentPlan;

      const isSandboxPlan = isSandboxLicense(addonLicense);
      if (isSandboxPlan) {
        handleOpenDialog(
          "Upgrade Plan Required",
          <UpgradeAllertDialog addonName={addon.name} />
        );
        return;
      }
      if (!addonLicense?.expiry_date || !addonLicense?.starts_at) {
        apiservice.showSnack(
          "Could not determine current plan expiry date",
          "error"
        );
        return;
      }
      const startDate = new Date(addonLicense.starts_at);
      const expiryDate = new Date(addonLicense.expiry_date);
      const today = new Date();
      let remainingDays = getAddonRemainingDays(expiryDate, today);
      if (remainingDays <= 0) remainingDays = 1;
      const cycleDays = getAddonCycleDays(startDate, expiryDate);
      if (remainingDays > cycleDays && cycleDays > 0) {
        remainingDays = cycleDays;
      }

      setDialogSize("medium");
      handleOpenDialog(
        "Confirm Add-on Purchase",
        <AddonPurchaseConfirmation
          addon={addon}
          remainingDays={remainingDays}
          cycleDays={cycleDays}
          onCancel={handleClose}
          onConfirm={(quantity: number) => {
            openAddonOrderSummary(addon, quantity);
          }}
        />
      );
    } catch (error: any) {
      apiservice.showSnack(
        "Failed to calculate addon cost locally",
        "error"
      );
    }
  };

  useEffect(() => {
    if (tenantName) {
      setAddonsLoading(true);
      Promise.all([fetchAvailableAddons(), fetchActiveAddons()]).finally(
        () => setAddonsLoading(false)
      );
    }
  }, [tenantName]);

  const availableAppCategories = React.useMemo(() => {
    const apps: any[] = [];
    const seenCodes = new Set<string>();
    availableAddons.forEach((addon: any) => {
      const app = addon.feature?.application;
      if (app?.code && !seenCodes.has(app.code)) {
        seenCodes.add(app.code);
        apps.push(app);
      }
    });
    return apps;
  }, [availableAddons]);

  const addonGroups = React.useMemo(() => {
    const groups: Record<
      string,
      { featureName: string; featureCode: string; addons: any[] }
    > = {};
    availableAddons
      .filter(
        (addon: any) =>
          !selectedApplicationCode ||
          addon.feature?.application?.code === selectedApplicationCode,
      )
      .forEach((addon: any) => {
      const fid = addon.feature_id || "unknown";
      if (!groups[fid]) {
        groups[fid] = {
          featureName: addon.feature?.name || "Other",
          featureCode: addon.feature?.code || "",
          addons: [],
        };
      }
      groups[fid].addons.push(addon);
    });
    return Object.values(groups);
  }, [availableAddons, selectedApplicationCode]);

  if (addonsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      <Typography variant="h4">Subscription Add-ons</Typography>
      <Typography variant="caption" color="text.secondary">
        Enhance your current plan with powerful add-ons. Their billing cycles
        perfectly synchronize with your base subscription.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
          <CheckCircleOutlineIcon
            sx={{ fontSize: 22, color: "success.main" }}
          />
          <Typography variant="h6" fontWeight={600} sx={{ whiteSpace: "nowrap"}}>
            Active Add-ons
          </Typography>
          {activeAddons.length > 0 && (
            <Chip
              label={`${activeAddons.length} active`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ ml: 1, fontWeight: 600, fontSize: "0.75rem" }}
            />
          )}
        </Box>

        {activeAddons.length > 0 ? (
          <Grid container spacing={2.5}>
            {activeAddons.map((addon: any) => (
              <Grid item xs={12} sm={6} lg={4} key={addon.id}>
                <ActiveAddons
                  addon={addon}
                  handleConfirmCancelAddon={handleConfirmCancelAddon}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              borderRadius: 2,
              bgcolor: alpha(theme.palette.action.hover, 0.3),
            }}
          >
            <WidgetsOutlinedIcon
              sx={{
                fontSize: 40,
                color: "text.disabled",
                mb: 1,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              You don't have any active add-ons yet.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Browse the catalog below to boost your plan limits.
            </Typography>
          </Box>
        )}
      </Paper>

      {selectedApplicationCode ? (
        <>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => {
              setSelectedApplicationCode(null);
              setActiveTab(0);
            }}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 3, fontSize: 13, fontWeight: 600 }}
          >
            ← Choose a different application
          </Link>
          <AvailableAddons
            canPurchase={canManagePayments}
            addonGroups={addonGroups}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleSimulateAddonLocal={handleSimulateAddonLocal}
          />
        </>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <ShoppingCartOutlinedIcon sx={{ fontSize: 22, color: "primary.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Available Add-ons
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Choose an application to browse the add-ons available for it.
          </Typography>

          {availableAppCategories.length === 0 ? (
            <Box
              sx={{
                py: 5,
                textAlign: "center",
                borderRadius: 2,
                bgcolor: alpha(theme.palette.action.hover, 0.3),
              }}
            >
              <WidgetsOutlinedIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No add-ons available at the moment.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {availableAppCategories.map((app: any) => {
                const meta = APP_CATEGORY_META[app.code] || DEFAULT_APP_CATEGORY_META;
                const activeCount = activeAddons.filter(
                  (a: any) => a.addon?.feature?.application?.code === app.code,
                ).length;
                return (
                  <Grid item xs={12} sm={6} md={4} key={app.code}>
                    <Box
                      onClick={() => setSelectedApplicationCode(app.code)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") setSelectedApplicationCode(app.code);
                      }}
                      sx={{
                        cursor: "pointer",
                        p: 3,
                        height: "100%",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: "primary.main",
                        }}
                      >
                        {meta.icon}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {app.name || app.code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {meta.description}
                        </Typography>
                      </Box>
                      {activeCount > 0 && (
                        <Chip
                          size="small"
                          label={`${activeCount} active`}
                          color="success"
                          variant="outlined"
                          sx={{ alignSelf: "flex-start" }}
                        />
                      )}
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ color: "primary.main", fontSize: 13, fontWeight: 600, mt: "auto" }}
                      >
                        View add-ons <ArrowForwardIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      <ArtDialog
        title={dialogData?.title}
        open={dialogOpen}
        onClose={handleClose}
        size={dialogSize}
        type="form"
        actionBuilder={() => <></>}
      >
        {dialogData?.content}
      </ArtDialog>
    </div>
  );
};

export default usePageWrapper(AddonsPage, {
  page_title: "Billings & Subscription",
});
